/**
 * SERVICE DETAIL MODAL
 * Click any service card on the Command Dashboard to open this.
 * Shows: live status, description, topology diagram (SVG radial),
 * real-time event topics (published / subscribed), and recent events.
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import {
  X, Activity, CheckCircle2, XCircle, Radio, RefreshCw,
  ArrowUpRight, ArrowDownLeft, Zap, ExternalLink,
} from 'lucide-react';
import type { ServiceDefinition } from '@/config/serviceRegistry';
import { SERVICE_REGISTRY, INFRA_SERVICES } from '@/config/serviceRegistry';
import { setVisibleInterval } from '@/utils/visibleInterval';

/** STANDBY = down by design in this profile (control API `standby`), not an outage. */
type HStatus = 'UP' | 'DOWN' | 'PROBING' | 'STANDBY';

/* ── All known event topics ─────────────────────────────────────────── */
const ALL_TOPICS = [
  { topic: 'price.observed',        layer: 'external',    color: '#f59e0b' },
  { topic: 'candle.closed',         layer: 'external',    color: '#f59e0b' },
  { topic: 'system.clock',          layer: 'external',    color: '#f59e0b' },
  { topic: 'user.command',          layer: 'external',    color: '#f59e0b' },
  { topic: 'structure.detected',    layer: 'perceptual',  color: '#10b981' },
  { topic: 'volatility.shift',      layer: 'perceptual',  color: '#10b981' },
  { topic: 'range.detected',        layer: 'perceptual',  color: '#10b981' },
  { topic: 'pattern.matched',       layer: 'perceptual',  color: '#10b981' },
  { topic: 'hypothesis.generated',  layer: 'cognitive',   color: '#00d4ff' },
  { topic: 'scenario.activated',    layer: 'cognitive',   color: '#00d4ff' },
  { topic: 'plan.proposed',         layer: 'cognitive',   color: '#00d4ff' },
  { topic: 'interpretation.made',   layer: 'cognitive',   color: '#00d4ff' },
  { topic: 'decision.committed',    layer: 'decision',    color: '#8b5cf6' },
  { topic: 'entry.signal.fired',    layer: 'decision',    color: '#8b5cf6' },
  { topic: 'exit.signal.fired',     layer: 'decision',    color: '#8b5cf6' },
  { topic: 'hold.decided',          layer: 'decision',    color: '#8b5cf6' },
  { topic: 'wait.decided',          layer: 'decision',    color: '#8b5cf6' },
  { topic: 'outcome.observed',      layer: 'outcome',     color: '#ec4899' },
  { topic: 'trade.filled.full',     layer: 'outcome',     color: '#ec4899' },
  { topic: 'trade.filled.partial',  layer: 'outcome',     color: '#ec4899' },
  { topic: 'stop.hit',              layer: 'outcome',     color: '#ec4899' },
  { topic: 'belief.updated',        layer: 'learning',    color: '#84cc16' },
  { topic: 'weight.adjusted',       layer: 'learning',    color: '#84cc16' },
  { topic: 'reward.applied',        layer: 'learning',    color: '#84cc16' },
  { topic: 'policy.violated',       layer: 'decision',    color: '#f43f5e' },
  { topic: 'policy.approved',       layer: 'decision',    color: '#22c55e' },
  { topic: 'explanation.generated', layer: 'cognitive',   color: '#e879f9' },
  { topic: 'shape.detected',        layer: 'perceptual',  color: '#14b8a6' },
];

/* ── Per-service topic declarations ─────────────────────────────────── */
const SERVICE_TOPICS: Record<string, { publishes: string[]; subscribes: string[] }> = {
  'price-observer':      { publishes: ['price.observed'],          subscribes: [] },
  'market-ingestion':    { publishes: ['price.observed'],          subscribes: [] },
  'candle-constructor':  { publishes: ['candle.closed'],           subscribes: ['price.observed'] },
  'perception':          { publishes: ['structure.detected', 'volatility.shift', 'range.detected', 'pattern.matched', 'shape.detected'], subscribes: ['candle.closed', 'price.observed'] },
  'shape-engine':        { publishes: ['shape.detected'],          subscribes: ['candle.closed', 'pattern.matched'] },
  'reasoning-engine':    { publishes: ['hypothesis.generated', 'scenario.activated'], subscribes: ['structure.detected', 'pattern.matched', 'shape.detected'] },
  'meaning-engine':      { publishes: ['interpretation.made'],     subscribes: ['structure.detected', 'pattern.matched', 'hypothesis.generated'] },
  'language-intelligence':{ publishes: ['interpretation.made'],   subscribes: ['user.command'] },
  'knowledge-graph':     { publishes: [],                          subscribes: ['interpretation.made', 'structure.detected'] },
  'ontology':            { publishes: [],                          subscribes: [] },
  'core-brain':          { publishes: ['plan.proposed', 'decision.committed'], subscribes: ['interpretation.made', 'hypothesis.generated', 'scenario.activated', 'pattern.matched'] },
  'learning-engine':     { publishes: ['belief.updated', 'weight.adjusted', 'reward.applied'], subscribes: ['outcome.observed', 'decision.committed'] },
  'memory':              { publishes: [],                          subscribes: ['candle.closed', 'structure.detected', 'decision.committed'] },
  'explanation-engine':  { publishes: ['explanation.generated'],   subscribes: ['decision.committed', 'plan.proposed'] },
  'simulation':          { publishes: ['candle.closed', 'outcome.observed'], subscribes: ['plan.proposed', 'decision.committed'] },
  'gateway':             { publishes: ['user.command'],            subscribes: [] },
  'policy-engine':       { publishes: ['policy.violated', 'policy.approved'], subscribes: ['decision.committed', 'plan.proposed'] },
  'schema-registry':     { publishes: [],                          subscribes: [] },
  'auth-system':         { publishes: [],                          subscribes: [] },
  'security-core':       { publishes: [],                          subscribes: [] },
  'brain-monitor':       { publishes: [],                          subscribes: ['*'] },
  'event-bus':           { publishes: ['*'],                       subscribes: ['*'] },
  'postgres':            { publishes: [],                          subscribes: [] },
  'redis':               { publishes: [],                          subscribes: [] },
  'mongo':               { publishes: [],                          subscribes: [] },
  'clickhouse':          { publishes: [],                          subscribes: [] },
  'neo4j':               { publishes: [],                          subscribes: [] },
  'prometheus':          { publishes: [],                          subscribes: [] },
};

/* ── Service topology (direct connections) ──────────────────────────── */
const TOPOLOGY: Record<string, string[]> = {
  'core-brain':          ['event-bus', 'reasoning-engine', 'perception', 'learning-engine', 'memory', 'meaning-engine', 'simulation', 'shape-engine'],
  'event-bus':           ['core-brain', 'perception', 'reasoning-engine', 'meaning-engine', 'language-intelligence', 'knowledge-graph', 'ontology', 'learning-engine', 'memory', 'explanation-engine', 'simulation', 'market-ingestion', 'candle-constructor', 'price-observer', 'gateway', 'schema-registry', 'policy-engine', 'brain-monitor', 'shape-engine'],
  'perception':          ['event-bus', 'candle-constructor', 'market-ingestion', 'knowledge-graph'],
  'reasoning-engine':    ['event-bus', 'knowledge-graph', 'ontology', 'memory'],
  'meaning-engine':      ['event-bus', 'ontology', 'knowledge-graph', 'language-intelligence'],
  'language-intelligence':['event-bus', 'meaning-engine'],
  'knowledge-graph':     ['event-bus', 'ontology', 'reasoning-engine', 'postgres', 'neo4j'],
  'ontology':            ['event-bus', 'knowledge-graph', 'schema-registry', 'postgres'],
  'shape-engine':        ['event-bus', 'candle-constructor', 'postgres'],
  'learning-engine':     ['event-bus', 'memory', 'simulation', 'postgres'],
  'memory':              ['event-bus', 'knowledge-graph', 'redis', 'postgres'],
  'explanation-engine':  ['event-bus', 'reasoning-engine'],
  'simulation':          ['event-bus', 'candle-constructor', 'market-ingestion'],
  'market-ingestion':    ['event-bus', 'candle-constructor', 'price-observer', 'clickhouse'],
  'candle-constructor':  ['event-bus', 'market-ingestion', 'clickhouse'],
  'price-observer':      ['event-bus', 'market-ingestion'],
  'gateway':             ['event-bus', 'auth-system'],
  'schema-registry':     ['event-bus', 'postgres'],
  'policy-engine':       ['event-bus', 'reasoning-engine'],
  'auth-system':         ['gateway', 'mongo', 'redis'],
  'security-core':       ['auth-system', 'gateway', 'redis'],
  'brain-monitor':       ['event-bus', 'core-brain', 'prometheus'],
  'postgres': [], 'redis': [], 'mongo': [], 'clickhouse': [], 'neo4j': [], 'prometheus': [],
};

const ALL_SVCS = [...SERVICE_REGISTRY, ...INFRA_SERVICES];

const COLOR_MAP: Record<string, string> = {
  'core-brain': '#00d4ff', 'event-bus': '#8b5cf6', 'perception': '#10b981',
  'reasoning-engine': '#f59e0b', 'meaning-engine': '#ec4899', 'memory': '#06b6d4',
  'language-intelligence': '#a855f7', 'knowledge-graph': '#f97316',
  'learning-engine': '#84cc16', 'explanation-engine': '#e879f9',
  'simulation': '#f43f5e', 'ontology': '#22d3ee', 'gateway': '#64748b',
  'auth-system': '#facc15', 'shape-engine': '#14b8a6', 'market-ingestion': '#fb923c',
  'candle-constructor': '#38bdf8', 'price-observer': '#ef4444',
  'schema-registry': '#94a3b8', 'policy-engine': '#818cf8',
  'security-core': '#fbbf24', 'brain-monitor': '#4ade80',
  'postgres': '#336791', 'redis': '#dc382d', 'mongo': '#47a248',
  'clickhouse': '#ffcc01', 'neo4j': '#018bff', 'prometheus': '#e6522c',
};

/* ── Radial Topology SVG ─────────────────────────────────────────────── */
function TopologyDiagram({ serviceId }: { serviceId: string }) {
  const connected = (TOPOLOGY[serviceId] ?? []).slice(0, 12); // cap at 12 for readability
  const cx = 200, cy = 200, r = 140;
  const color = COLOR_MAP[serviceId] || '#64748b';

  return (
    <svg width="400" height="400" style={{ display: 'block' }}>
      <defs>
        <radialGradient id="center-glow" cx="50%" cy="50%">
          <stop offset="0%" stopColor={color} stopOpacity="0.25" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </radialGradient>
        {/* Animated dash offset for trace lines */}
        <style>{`
          .trace-line { stroke-dasharray: 6 4; animation: trace-anim 2s linear infinite; }
          @keyframes trace-anim { to { stroke-dashoffset: -20; } }
          .node-ring { animation: ring-pulse 3s ease-in-out infinite; }
          @keyframes ring-pulse { 0%,100%{opacity:0.4} 50%{opacity:1} }
        `}</style>
      </defs>

      {/* Background glow */}
      <circle cx={cx} cy={cy} r={70} fill="url(#center-glow)" />

      {/* Connection lines */}
      {connected.map((id, i) => {
        const angle = (2 * Math.PI * i) / connected.length - Math.PI / 2;
        const nx = cx + r * Math.cos(angle);
        const ny = cy + r * Math.sin(angle);
        const nc = COLOR_MAP[id] || '#475569';
        return (
          <line
            key={`line-${id}`}
            className="trace-line"
            x1={cx} y1={cy} x2={nx} y2={ny}
            stroke={nc}
            strokeWidth="1.5"
            strokeOpacity="0.5"
          />
        );
      })}

      {/* Central service node */}
      <circle cx={cx} cy={cy} r={36} fill={`${color}18`} stroke={color} strokeWidth="1.5" />
      <circle cx={cx} cy={cy} r={42} fill="none" stroke={color} strokeWidth="0.5" strokeDasharray="4 4" className="node-ring" />
      <text x={cx} y={cy + 4} textAnchor="middle" fill={color} fontSize="9" fontFamily="'JetBrains Mono', monospace" fontWeight="700">
        {serviceId.replace(/-/g, '\u00ad').toUpperCase().substring(0, 12)}
      </text>

      {/* Connected nodes */}
      {connected.map((id, i) => {
        const angle = (2 * Math.PI * i) / connected.length - Math.PI / 2;
        const nx = cx + r * Math.cos(angle);
        const ny = cy + r * Math.sin(angle);
        const nc = COLOR_MAP[id] || '#475569';
        const name = ALL_SVCS.find(s => s.id === id)?.name ?? id;
        const shortName = name.replace(/\s+/g, '\n').substring(0, 18);
        return (
          <g key={id}>
            <circle cx={nx} cy={ny} r={20} fill={`${nc}15`} stroke={nc} strokeWidth="1" />
            <text x={nx} y={ny + 4} textAnchor="middle" fill={nc} fontSize="7" fontFamily="'JetBrains Mono', monospace">
              {name.substring(0, 10)}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

/* ── Live Event Feed ─────────────────────────────────────────────────── */
function LiveEvents({ serviceId }: { serviceId: string }) {
  const [events, setEvents] = useState<Array<{ time: string; topic: string; dir: string; data: string }>>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    const fetch_ = async () => {
      try {
        const res = await fetch(
          `/svc/event-bus/api/v1/events/recent?service=${serviceId}&limit=20`,
          { signal: AbortSignal.timeout(3000) },
        );
        if (res.ok && active) {
          const data = await res.json();
          const raw: any[] = Array.isArray(data) ? data : data.events || data.items || [];
          setEvents(
            raw.slice(0, 20).map((e: any) => ({
              time: new Date(e.timestamp || Date.now()).toLocaleTimeString('en-US', { hour12: false }),
              topic: e.type || e.topic || e.event_type || 'unknown',
              dir: e.source === serviceId ? 'pub' : 'sub',
              data: JSON.stringify(e.data || e.payload || {}).substring(0, 80),
            })),
          );
        }
      } catch { /* offline */ }
      if (active) setLoading(false);
    };
    fetch_();
    const iv = setVisibleInterval(fetch_, 3000);
    return () => { active = false; iv(); };
  }, [serviceId]);

  if (loading) return <div style={{ padding: '1rem', color: '#334155', fontSize: '0.6875rem', fontFamily: "'JetBrains Mono', monospace" }}>Connecting to event bus…</div>;
  if (events.length === 0) {
    return (
      <div style={{ padding: '1rem', fontSize: '0.625rem', color: '#334155', fontFamily: "'JetBrains Mono', monospace" }}>
        No recent events — service may be idle or event bus is offline.
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {events.map((e, i) => {
        const topicMeta = ALL_TOPICS.find(t => t.topic === e.topic);
        const tc = topicMeta?.color || '#64748b';
        return (
          <div key={i} style={{ display: 'flex', gap: '0.5rem', padding: '0.3rem 0.5rem', background: 'rgba(255,255,255,0.02)', borderRadius: 4, fontSize: '0.5625rem', fontFamily: "'JetBrains Mono', monospace", borderLeft: `2px solid ${tc}40` }}>
            <span style={{ color: '#334155', flexShrink: 0 }}>{e.time}</span>
            <span style={{ flexShrink: 0, padding: '0 4px', borderRadius: 3, background: e.dir === 'pub' ? 'rgba(139,92,246,0.15)' : 'rgba(16,185,129,0.12)', color: e.dir === 'pub' ? '#8b5cf6' : '#10b981' }}>
              {e.dir === 'pub' ? '↑ PUB' : '↓ SUB'}
            </span>
            <span style={{ color: tc, flexShrink: 0 }}>{e.topic}</span>
            <span style={{ color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.data}</span>
          </div>
        );
      })}
    </div>
  );
}

/* ── Event Bus All Topics View ──────────────────────────────────────── */
function EventBusTopics() {
  const [liveTopics, setLiveTopics] = useState<Array<{ topic: string; publishers: number; subscribers: number; msgSec: number; lastMsg: string }>>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    const fetch_ = async () => {
      try {
        // event-bus GET /api/v2/topics -> {topics: [...]} (names, or objects with counters)
        const res = await fetch('/svc/event-bus/api/v2/topics', { signal: AbortSignal.timeout(3000) });
        if (res.ok && active) {
          const data = await res.json();
          const raw: any[] = Array.isArray(data) ? data : data.topics || [];
          const topics = raw.map((t: any) => (typeof t === 'string' ? { topic: t } : t));
          setLiveTopics(topics.map((t: any) => ({
            topic: t.topic || t.name || t.id,
            publishers: t.publishers ?? t.publisher_count ?? 0,
            subscribers: t.subscribers ?? t.subscriber_count ?? 0,
            msgSec: t.messages_per_second ?? t.rate ?? 0,
            lastMsg: t.last_message ? new Date(t.last_message).toLocaleTimeString('en-US', { hour12: false }) : '—',
          })));
          setLoading(false);
          return;
        }
      } catch { /* offline */ }
      // Fallback: show static schema topics
      setLiveTopics(ALL_TOPICS.map(t => ({
        topic: t.topic, publishers: 0, subscribers: 0, msgSec: 0, lastMsg: '—',
      })));
      setLoading(false);
    };
    fetch_();
    const iv = setVisibleInterval(fetch_, 2000);
    return () => { active = false; iv(); };
  }, []);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 3, overflow: 'auto', maxHeight: 320 }}>
      {/* Header */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 60px 60px 70px 70px', gap: '0.25rem', padding: '0.25rem 0.5rem', borderBottom: '1px solid rgba(255,255,255,0.06)', fontSize: '0.45rem', color: '#334155', letterSpacing: '0.1em', fontFamily: "'JetBrains Mono', monospace" }}>
        <span>TOPIC</span><span>PUBS</span><span>SUBS</span><span>MSG/S</span><span>LAST MSG</span>
      </div>
      {liveTopics.map((t) => {
        const meta = ALL_TOPICS.find(x => x.topic === t.topic);
        const color = meta?.color || '#475569';
        const layer = meta?.layer || 'unknown';
        return (
          <div key={t.topic} style={{ display: 'grid', gridTemplateColumns: '1fr 60px 60px 70px 70px', gap: '0.25rem', padding: '0.25rem 0.5rem', background: 'rgba(255,255,255,0.015)', borderRadius: 3, fontSize: '0.5rem', fontFamily: "'JetBrains Mono', monospace" }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: color, display: 'inline-block', flexShrink: 0 }} />
              <span style={{ color: '#94a3b8' }}>{t.topic}</span>
              <span style={{ fontSize: '0.4rem', color: '#1e293b', background: 'rgba(255,255,255,0.04)', padding: '1px 4px', borderRadius: 3 }}>{layer}</span>
            </div>
            <span style={{ color: t.publishers > 0 ? '#8b5cf6' : '#1e293b' }}>{t.publishers}</span>
            <span style={{ color: t.subscribers > 0 ? '#10b981' : '#1e293b' }}>{t.subscribers}</span>
            <span style={{ color: t.msgSec > 0 ? '#f59e0b' : '#1e293b' }}>{t.msgSec > 0 ? t.msgSec.toFixed(1) : '—'}</span>
            <span style={{ color: '#334155' }}>{t.lastMsg}</span>
          </div>
        );
      })}
    </div>
  );
}

/* ── Main Modal ─────────────────────────────────────────────────────── */
export interface ServiceDetailModalProps {
  service: ServiceDefinition;
  statuses: Record<string, HStatus>;
  onClose: () => void;
}

export default function ServiceDetailModal({ service, statuses, onClose }: ServiceDetailModalProps) {
  const [tab, setTab] = useState<'topology' | 'topics' | 'events'>('topology');
  const color = COLOR_MAP[service.id] || '#64748b';
  const status = statuses[service.id] ?? 'PROBING';
  const topics = SERVICE_TOPICS[service.id] ?? { publishes: [], subscribes: [] };
  const isEventBus = service.id === 'event-bus';

  // ESC to close
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const statusColor = status === 'UP' ? '#22c55e' : status === 'DOWN' ? '#ef4444' : status === 'STANDBY' ? '#60a5fa' : '#f59e0b';
  const statusLabel = status === 'UP' ? 'ONLINE' : status === 'DOWN' ? 'OFFLINE' : status === 'STANDBY' ? 'STANDBY' : 'PROBING';

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(2, 5, 10, 0.88)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        backdropFilter: 'blur(4px)',
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <style>{`
        @keyframes modal-in { from { opacity:0; transform:scale(0.96) translateY(8px) } to { opacity:1; transform:scale(1) translateY(0) } }
        @keyframes trace-anim { to { stroke-dashoffset: -20; } }
        @keyframes ring-pulse { 0%,100%{opacity:0.35} 50%{opacity:0.9} }
      `}</style>

      <div
        style={{
          width: '90vw', maxWidth: 960,
          maxHeight: '88vh',
          background: 'linear-gradient(145deg, #060e1c 0%, #04080f 100%)',
          border: `1px solid ${color}40`,
          borderRadius: 12,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          animation: 'modal-in 0.2s ease',
          boxShadow: `0 0 60px ${color}15, 0 0 120px rgba(0,0,0,0.8)`,
        }}
      >
        {/* ── Modal Header ── */}
        <div
          style={{
            padding: '1rem 1.25rem',
            borderBottom: `1px solid ${color}25`,
            display: 'flex',
            alignItems: 'center',
            gap: '1rem',
            background: `linear-gradient(90deg, ${color}08 0%, transparent 100%)`,
          }}
        >
          {/* Service ID badge */}
          <div
            style={{
              padding: '0.25rem 0.625rem',
              background: `${color}18`,
              border: `1px solid ${color}35`,
              borderRadius: 5,
              fontSize: '0.5625rem',
              fontFamily: "'JetBrains Mono', monospace",
              color,
              letterSpacing: '0.08em',
              fontWeight: 700,
              flexShrink: 0,
            }}
          >
            {service.id.toUpperCase()}
          </div>

          <div style={{ flex: 1 }}>
            <div style={{ fontSize: '1rem', fontWeight: 700, color: '#e2e8f0', letterSpacing: '0.04em' }}>
              {service.name}
            </div>
            <div style={{ fontSize: '0.5625rem', color: '#475569', marginTop: 2, fontFamily: "'JetBrains Mono', monospace" }}>
              {service.description}
            </div>
          </div>

          {/* Status */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4, flexShrink: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: statusColor, display: 'inline-block', boxShadow: `0 0 6px ${statusColor}` }} />
              <span style={{ fontSize: '0.625rem', fontWeight: 700, color: statusColor, fontFamily: "'JetBrains Mono', monospace", letterSpacing: '0.1em' }}>{statusLabel}</span>
            </div>
            <div style={{ fontSize: '0.5rem', color: '#334155', fontFamily: "'JetBrains Mono', monospace" }}>
              {service.stack} · :{service.port}
            </div>
          </div>

          {/* Close */}
          <button
            onClick={onClose}
            style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 6, color: '#475569', cursor: 'pointer', padding: '0.375rem', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}
          >
            <X size={16} />
          </button>
        </div>

        {/* ── Tab Bar ── */}
        <div style={{ display: 'flex', gap: 0, borderBottom: `1px solid ${color}20`, padding: '0 1.25rem' }}>
          {([
            { key: 'topology', label: 'TOPOLOGY' },
            { key: 'topics',   label: isEventBus ? 'ALL TOPICS (LIVE)' : 'TOPICS' },
            { key: 'events',   label: 'LIVE EVENTS' },
          ] as const).map(({ key, label }) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              style={{
                padding: '0.5rem 1rem',
                background: 'transparent',
                border: 'none',
                borderBottom: `2px solid ${tab === key ? color : 'transparent'}`,
                color: tab === key ? color : '#334155',
                fontSize: '0.5625rem',
                fontWeight: 600,
                cursor: 'pointer',
                letterSpacing: '0.1em',
                fontFamily: "'JetBrains Mono', monospace",
                transition: 'all 150ms',
              }}
            >
              {label}
            </button>
          ))}
        </div>

        {/* ── Tab Content ── */}
        <div style={{ flex: 1, overflow: 'auto', padding: '1rem 1.25rem', display: 'flex', gap: '1.25rem' }}>
          {tab === 'topology' && (
            <>
              {/* SVG diagram */}
              <div
                style={{
                  flexShrink: 0,
                  background: 'linear-gradient(135deg, #06111e 0%, #04080f 100%)',
                  border: `1px solid ${color}20`,
                  borderRadius: 10,
                  overflow: 'hidden',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <TopologyDiagram serviceId={service.id} />
              </div>

              {/* Connected services list */}
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '0.625rem' }}>
                <div style={{ fontSize: '0.5rem', color: '#334155', letterSpacing: '0.12em', fontFamily: "'JetBrains Mono', monospace" }}>
                  CONNECTED SERVICES ({(TOPOLOGY[service.id] ?? []).length})
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem', overflow: 'auto' }}>
                  {(TOPOLOGY[service.id] ?? []).map(id => {
                    const svc = ALL_SVCS.find(s => s.id === id);
                    if (!svc) return null;
                    const nc = COLOR_MAP[id] || '#475569';
                    const ns = statuses[id] ?? 'PROBING';
                    return (
                      <div key={id} style={{ display: 'flex', alignItems: 'center', gap: '0.625rem', padding: '0.4rem 0.625rem', background: 'rgba(255,255,255,0.02)', border: `1px solid ${nc}18`, borderRadius: 5 }}>
                        <span style={{ width: 6, height: 6, borderRadius: '50%', background: ns === 'UP' ? '#22c55e' : ns === 'DOWN' ? '#ef4444' : ns === 'STANDBY' ? '#60a5fa' : '#475569', display: 'inline-block', flexShrink: 0 }} />
                        <div style={{ flex: 1 }}>
                          <span style={{ fontSize: '0.6875rem', color: '#94a3b8', fontFamily: "'JetBrains Mono', monospace" }}>{svc.name}</span>
                          <span style={{ fontSize: '0.5rem', color: '#334155', fontFamily: "'JetBrains Mono', monospace", marginLeft: 6 }}>:{svc.port}</span>
                        </div>
                        <span style={{ fontSize: '0.5rem', color: '#1e293b', fontFamily: "'JetBrains Mono', monospace" }}>{svc.stack}</span>
                      </div>
                    );
                  })}
                  {(TOPOLOGY[service.id] ?? []).length === 0 && (
                    <div style={{ color: '#1e293b', fontSize: '0.5625rem', fontFamily: "'JetBrains Mono', monospace" }}>
                      No direct connections defined.
                    </div>
                  )}
                </div>
              </div>
            </>
          )}

          {tab === 'topics' && (
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {isEventBus ? (
                <>
                  <div style={{ fontSize: '0.5rem', color: '#334155', letterSpacing: '0.12em', fontFamily: "'JetBrains Mono', monospace", display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ width: 5, height: 5, borderRadius: '50%', background: '#22c55e', display: 'inline-block', animation: 'led-pulse 2s infinite' }} />
                    ALL SYSTEM TOPICS — LIVE DATA
                  </div>
                  <EventBusTopics />
                </>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                  {/* Publishes */}
                  <div>
                    <div style={{ fontSize: '0.5rem', color: '#8b5cf6', letterSpacing: '0.12em', fontFamily: "'JetBrains Mono', monospace", marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: 5 }}>
                      <ArrowUpRight size={11} /> PUBLISHES ({topics.publishes.length})
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
                      {topics.publishes.length === 0
                        ? <span style={{ fontSize: '0.5625rem', color: '#1e293b', fontFamily: "'JetBrains Mono', monospace" }}>No publications</span>
                        : topics.publishes.map(tp => {
                          const meta = ALL_TOPICS.find(t => t.topic === tp);
                          return (
                            <div key={tp} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '0.375rem 0.625rem', background: 'rgba(139,92,246,0.06)', border: '1px solid rgba(139,92,246,0.15)', borderRadius: 5 }}>
                              <span style={{ width: 6, height: 6, borderRadius: '50%', background: meta?.color || '#8b5cf6', display: 'inline-block' }} />
                              <span style={{ fontSize: '0.5625rem', color: '#94a3b8', fontFamily: "'JetBrains Mono', monospace" }}>{tp}</span>
                              {meta && <span style={{ fontSize: '0.4rem', color: '#334155', marginLeft: 'auto', fontFamily: "'JetBrains Mono', monospace" }}>{meta.layer}</span>}
                            </div>
                          );
                        })
                      }
                      {topics.publishes[0] === '*' && (
                        <div style={{ padding: '0.375rem 0.625rem', background: 'rgba(139,92,246,0.06)', border: '1px solid rgba(139,92,246,0.15)', borderRadius: 5, fontSize: '0.5625rem', color: '#8b5cf6', fontFamily: "'JetBrains Mono', monospace" }}>
                          All topics (event router)
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Subscribes */}
                  <div>
                    <div style={{ fontSize: '0.5rem', color: '#10b981', letterSpacing: '0.12em', fontFamily: "'JetBrains Mono', monospace", marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: 5 }}>
                      <ArrowDownLeft size={11} /> SUBSCRIBES ({topics.subscribes.length})
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
                      {topics.subscribes.length === 0
                        ? <span style={{ fontSize: '0.5625rem', color: '#1e293b', fontFamily: "'JetBrains Mono', monospace" }}>No subscriptions</span>
                        : topics.subscribes.map(tp => {
                          const meta = ALL_TOPICS.find(t => t.topic === tp);
                          return (
                            <div key={tp} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '0.375rem 0.625rem', background: 'rgba(16,185,129,0.06)', border: '1px solid rgba(16,185,129,0.15)', borderRadius: 5 }}>
                              <span style={{ width: 6, height: 6, borderRadius: '50%', background: meta?.color || '#10b981', display: 'inline-block' }} />
                              <span style={{ fontSize: '0.5625rem', color: '#94a3b8', fontFamily: "'JetBrains Mono', monospace" }}>{tp}</span>
                              {meta && <span style={{ fontSize: '0.4rem', color: '#334155', marginLeft: 'auto', fontFamily: "'JetBrains Mono', monospace" }}>{meta.layer}</span>}
                            </div>
                          );
                        })
                      }
                      {topics.subscribes[0] === '*' && (
                        <div style={{ padding: '0.375rem 0.625rem', background: 'rgba(16,185,129,0.06)', border: '1px solid rgba(16,185,129,0.15)', borderRadius: 5, fontSize: '0.5625rem', color: '#10b981', fontFamily: "'JetBrains Mono', monospace" }}>
                          All topics (full observer)
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {tab === 'events' && (
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '0.625rem' }}>
              <div style={{ fontSize: '0.5rem', color: '#334155', letterSpacing: '0.12em', fontFamily: "'JetBrains Mono', monospace", display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ width: 5, height: 5, borderRadius: '50%', background: '#22c55e', display: 'inline-block' }} />
                LIVE EVENT STREAM — {service.name.toUpperCase()}
              </div>
              <LiveEvents serviceId={service.id} />
            </div>
          )}
        </div>

        {/* ── Footer ── */}
        <div
          style={{
            padding: '0.5rem 1.25rem',
            borderTop: `1px solid ${color}15`,
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
            fontSize: '0.5rem',
            color: '#1e293b',
            fontFamily: "'JetBrains Mono', monospace",
          }}
        >
          <span style={{ color: '#334155' }}>Stack: {service.stack}</span>
          <span>·</span>
          <span>Port: {service.port}</span>
          <span>·</span>
          <span>Category: {service.category}</span>
          <span style={{ marginLeft: 'auto', color: '#334155' }}>ESC to close</span>
        </div>
      </div>
    </div>
  );
}
